import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import {
  PedidoRow,
  PedidoDetalle,
  PedidoEstado,
  CreatePedidoDto,
  UpdatePedidoDto,
  AddMaterialToPedidoDto,
  AddEquipoToPedidoDto,
  CreateDevolucionDto,
  DevolucionDetalle,
  DevolucionRow,
  EquipoEstadoItem,
} from '../models/pedido.models';
import { ApiResponse } from '../../../core/models/api-response.model';
import { catchError, map, Observable, throwError } from 'rxjs';
import { PedidoQueryParams, PaginationInfo } from '../models/pedido-query.model';

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationInfo;
}

const DEFAULT_PAGINATION: PaginationInfo = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
};

@Injectable({
  providedIn: 'root',
})
export class PedidoApiService {
  private http = inject(HttpClient);
  private base = environment.apiUrl;

  // ==================== PEDIDOS CRUD ====================

  getAllPedidos(filters?: PedidoQueryParams): Observable<PaginatedResponse<PedidoRow>> {
    const params = this.buildParams(filters);

    return this.http
      .get<ApiResponse<PedidoRow[]>>(`${this.base}/pedidos`, { params })
      .pipe(
        map((res): PaginatedResponse<PedidoRow> => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return {
            data: res.data,
            pagination: res.pagination ?? DEFAULT_PAGINATION,
          };
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo obtener la lista de pedidos';
          return throwError(() => new Error(msg));
        })
      );
  }

  getPedidoById(id: number): Observable<PedidoDetalle> {
    return this.http
      .get<ApiResponse<PedidoDetalle>>(`${this.base}/pedidos/${id}`)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo obtener el pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  createPedido(data: CreatePedidoDto): Observable<PedidoDetalle> {
    return this.http
      .post<ApiResponse<PedidoDetalle>>(`${this.base}/pedidos`, data)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo crear el pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  updatePedido(id: number, data: UpdatePedidoDto): Observable<PedidoDetalle> {
    return this.http
      .put<ApiResponse<PedidoDetalle>>(`${this.base}/pedidos/${id}`, data)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo actualizar el pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  // ==================== ITEMS DEL PEDIDO ====================

  addMaterial(pedidoId: number, data: AddMaterialToPedidoDto): Observable<PedidoDetalle> {
    return this.http
      .post<ApiResponse<PedidoDetalle>>(`${this.base}/pedidos/${pedidoId}/materiales`, data)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo agregar el material al pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  removeMaterial(pedidoId: number, materialId: number): Observable<PedidoDetalle> {
    return this.http
      .delete<ApiResponse<PedidoDetalle>>(`${this.base}/pedidos/${pedidoId}/materiales/${materialId}`)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo quitar el material del pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  addEquipo(pedidoId: number, data: AddEquipoToPedidoDto): Observable<PedidoDetalle> {
    return this.http
      .post<ApiResponse<PedidoDetalle>>(`${this.base}/pedidos/${pedidoId}/equipos`, data)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo agregar el equipo al pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  removeEquipo(pedidoId: number, equipoId: number): Observable<PedidoDetalle> {
    return this.http
      .delete<ApiResponse<PedidoDetalle>>(`${this.base}/pedidos/${pedidoId}/equipos/${equipoId}`)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo quitar el equipo del pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  // ==================== ACCIONES DE ESTADO ====================

  entregarPedido(id: number, entregadoPor?: string): Observable<PedidoDetalle> {
    return this.http
      .post<ApiResponse<PedidoDetalle>>(`${this.base}/pedidos/${id}/entregar`, { entregado_por: entregadoPor })
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo entregar el pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  cancelarPedido(id: number): Observable<PedidoDetalle> {
    return this.http
      .post<ApiResponse<PedidoDetalle>>(`${this.base}/pedidos/${id}/cancelar`, {})
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo cancelar el pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  // ==================== DEVOLUCIONES ====================

  registrarDevolucion(pedidoId: number, data: CreateDevolucionDto): Observable<DevolucionDetalle> {
    return this.http
      .post<ApiResponse<DevolucionDetalle>>(`${this.base}/pedidos/${pedidoId}/devolucion`, data)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo registrar la devolución';
          return throwError(() => new Error(msg));
        })
      );
  }

  getDevolucionesByPedido(pedidoId: number): Observable<DevolucionRow[]> {
    return this.http
      .get<ApiResponse<DevolucionRow[]>>(`${this.base}/pedidos/${pedidoId}/devoluciones`)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo obtener las devoluciones';
          return throwError(() => new Error(msg));
        })
      );
  }

  getEquiposEstados(): Observable<EquipoEstadoItem[]> {
    return this.http
      .get<ApiResponse<EquipoEstadoItem[]>>(`${this.base}/pedidos/equipos-estados`)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo obtener los estados de equipos';
          return throwError(() => new Error(msg));
        })
      );
  }

  // ==================== CATÁLOGOS ====================

  getEstados(): Observable<PedidoEstado[]> {
    return this.http
      .get<ApiResponse<PedidoEstado[]>>(`${this.base}/pedidos/estados`)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message ?? 'Error API');
          return res.data;
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'No se pudo obtener los estados de pedido';
          return throwError(() => new Error(msg));
        })
      );
  }

  // ==================== HELPERS ====================

  private buildParams(filters?: PedidoQueryParams): HttpParams {
    let params = new HttpParams();
    if (!filters) return params;

    const setIf = (key: keyof PedidoQueryParams, value: unknown) => {
      if (value === null || value === undefined) return;
      const str = String(value).trim();
      if (str === '') return;
      params = params.set(String(key), str);
    };

    setIf('estado_id', filters.estado_id);
    setIf('search', filters.search);
    setIf('page', filters.page);
    setIf('limit', filters.limit);

    return params;
  }
}
